import { strapiService } from './api';

/**
 * Rate limiting for login attempts with enhanced debugging
 */
const loginAttemptTracker = {
  attempts: 0,
  lastAttempt: null,
  isBlocked: false,
  blockUntil: null,
  testCredentialsUsed: new Set(),
  
  // Track failed login attempt - Fixed to not flag legitimate admin emails
  recordFailedAttempt(identifier) {
    this.attempts += 1;
    this.lastAttempt = Date.now();
    
    // More specific test credentials detection - only flag obvious test patterns
    // Removed 'admin' from test identifiers to allow legitimate admin emails
    const testIdentifiers = ['test@test.com', 'demo@demo.com', 'user@test.com', 'gdgdfgdsfg'];
    if (testIdentifiers?.some(test => identifier?.toLowerCase() === test)) {
      this.testCredentialsUsed?.add(identifier);
    }
    
    // Block after 5 failed attempts for 3 minutes
    if (this.attempts >= 5) {
      this.isBlocked = true;
      this.blockUntil = Date.now() + (3 * 60 * 1000);
    }
  },
  
  // Check if login is currently blocked
  isLoginBlocked() {
    if (this.isBlocked && this.blockUntil && Date.now() > this.blockUntil) {
      // Reset after block period expires
      this.reset();
      return false;
    }
    return this.isBlocked;
  },
  
  // Get remaining block time in minutes
  getBlockTimeRemaining() {
    if (!this.isBlocked || !this.blockUntil) return 0;
    const remaining = Math.ceil((this.blockUntil - Date.now()) / (60 * 1000));
    return Math.max(0, remaining);
  },
  
  // Check if test credentials were used
  hasUsedTestCredentials() {
    return this.testCredentialsUsed?.size > 0;
  },
  
  // Get helpful message for test credentials
  getTestCredentialsMessage() {
    if (this.hasUsedTestCredentials()) {
      return 'Se detectaron credenciales de prueba. Use credenciales válidas del sistema Strapi.';
    }
    return null;
  },
  
  // Reset attempt tracking
  reset() {
    this.attempts = 0;
    this.lastAttempt = null;
    this.isBlocked = false;
    this.blockUntil = null;
    this.testCredentialsUsed?.clear();
  },
  
  // Manual reset for development/testing (can be called from browser console)
  manualReset() {
    console.log('🔄 Resetting login attempts manually...');
    this.reset();
    console.log('✅ Login attempts reset successfully');
  }
};

// Make manual reset available globally for development
if (typeof window !== 'undefined') {
  window.resetLoginAttempts = () => loginAttemptTracker?.manualReset();
}

/**
 * Traduce errores nativos de Strapi a mensajes en español con contexto mejorado
 */
const translateStrapiError = (errorMessage = '', statusCode = null, context = {}) => {
  const message = errorMessage?.toLowerCase()?.trim();
  
  // Enhanced error translations with more context
  const errorTranslations = {
    // Credenciales incorrectas - versiones más específicas
    'invalid identifier or password': 'Usuario o contraseña incorrectos',
    'identifier or password invalid': 'Usuario o contraseña incorrectos', 
    'wrong credentials provided': 'Las credenciales proporcionadas son incorrectas',
    'authentication failed': 'Falló la autenticación - verifique sus credenciales',
    'login failed': 'Error al iniciar sesión - credenciales inválidas',
    'invalid credentials': 'Credenciales inválidas',
    'invalid login credentials': 'Credenciales de acceso incorrectas',
    'invalid user credentials': 'Credenciales de usuario incorrectas',
    
    // Errores de usuario específicos
    'user not found': 'Usuario no encontrado en el sistema',
    'user does not exist': 'El usuario especificado no existe',
    'user is not active': 'El usuario no está activo',
    'user account is blocked': 'La cuenta de usuario está bloqueada',
    'user account is disabled': 'La cuenta de usuario está deshabilitada',
    
    // Errores de formato de entrada
    'invalid email format': 'El formato del correo electrónico es inválido',
    'email is required': 'El correo electrónico es requerido',
    'username is required': 'El nombre de usuario es requerido', 
    'identifier is required': 'El identificador es requerido',
    'password is required': 'La contraseña es requerida',
    
    // Errores de servidor y red
    'network error': 'Error de conexión. Verifique su conexión a internet',
    'server error': 'Error del servidor. Inténtelo más tarde',
    'service unavailable': 'Servicio no disponible temporalmente',
    'too many requests': 'Demasiadas solicitudes. Espere antes de intentar nuevamente',
    'timeout': 'La solicitud ha excedido el tiempo límite',
    
    // Errores de validación
    'validation error': 'Error de validación en los datos proporcionados',
    'validationerror': 'Error de validación en los datos proporcionados',
    'bad request': 'Solicitud incorrecta - verifique los datos ingresados',
    'invalid request': 'Solicitud inválida',
    'missing required fields': 'Faltan campos requeridos',
    'invalid input': 'Los datos de entrada son inválidos',
  };
  
  // Search for exact match first
  if (errorTranslations?.[message]) {
    return errorTranslations?.[message];
  }
  
  // Search for partial matches
  for (const [key, translation] of Object.entries(errorTranslations)) {
    if (message?.includes(key)) {
      return translation;
    }
  }
  
  // Enhanced status code based errors with more helpful messages
  switch (statusCode) {
    case 400:
      // Check if test credentials were used
      if (context?.hasTestCredentials) {
        return 'Credenciales de prueba detectadas. Use credenciales válidas de su cuenta Strapi.';
      }
      return 'Credenciales incorrectas. Verifique que su usuario y contraseña sean correctos.';
    case 401:
      return 'No autorizado. Las credenciales proporcionadas son inválidas.';
    case 403:
      return 'Acceso denegado. Verifique que su cuenta tenga los permisos necesarios.';
    case 404:
      return 'Usuario no encontrado. Verifique que el usuario existe en el sistema.';
    case 409:
      return 'El usuario o correo electrónico ya existe en el sistema.';
    case 429:
      return 'Demasiadas solicitudes. Por favor espere antes de intentar nuevamente.';
    case 500:
      return 'Error interno del servidor. Contacte al administrador si persiste.';
    case 502:
      return 'Error de conexión con el servidor. Verifique la configuración de red.';
    case 503:
      return 'El servicio no está disponible temporalmente. Inténtelo más tarde.';
    default:
      break;
  }
  
  // Return contextual generic message
  if (context?.hasTestCredentials) {
    return 'Se detectaron credenciales de prueba. Use credenciales válidas para acceder al sistema.';
  }
  
  return 'Credenciales incorrectas. Verifique su usuario y contraseña e inténtelo nuevamente.';
};

/**
 * Map Strapi user_type to application roles according to new API documentation
 * Updated to handle super_admin, company_admin, employee types from user.user_type field
 */
const mapStrapiRoleToAppRole = (userType, userEmail) => {
  // New role mapping according to API documentation
  const roleMapping = {
    'super_admin': 'administrator',
    'company_admin': 'company', 
    'employee': 'employee',
    'authenticated': 'administrator' // Default fallback for authenticated users
  };
  
  // Use user_type field from API response
  if (userType && roleMapping?.[userType]) {
    console.log('Role mapped from user_type:', { userType, mappedRole: roleMapping?.[userType] });
    return roleMapping?.[userType];
  }
  
  // Email-based role detection as fallback
  if (userEmail?.toLowerCase()?.includes('admin') || 
      userEmail?.toLowerCase()?.includes('administrador') ||
      userEmail?.toLowerCase()?.includes('superadmin')) {
    return 'administrator';
  }
  
  // Check if it's a company email pattern
  if (userEmail?.includes('@') && !userEmail?.includes('admin')) {
    return 'company';
  }
  
  // Default to administrator for authenticated users
  return 'administrator';
};

/**
 * Get appropriate redirect route based on user role according to API documentation
 */
const getRoleBasedRedirectRoute = (userType) => {
  const roleRoutes = {
    'super_admin': '/app/system-analytics',      // Super admin sees all companies
    'company_admin': '/app/company-management',   // Company admin sees their company
    'employee': '/app/employee-payroll-portal'    // Employee sees only their data
  };

  return roleRoutes?.[userType] || '/app/system-analytics'; // Default to system analytics
};

/**
 * Enhanced Authentication service for Strapi v5 integration with comprehensive diagnostics
 */
export const authService = {
  /**
   * Establece la sesión a partir de un {jwt, user} ya emitido por el backend
   * (p.ej. el magic-link de /entrar). Normaliza el user_data con el MISMO shape
   * que el login normal (incluye `role` mapeado, que los guards necesitan) y
   * devuelve la ruta de redirección según el rol.
   */
  establishSessionFromToken: (jwt, user) => {
    const userType = user?.user_type || 'authenticated';
    const mappedRole = mapStrapiRoleToAppRole(userType, user?.email);
    const userData = {
      id: user?.id,
      documentId: user?.documentId,
      username: user?.username,
      email: user?.email,
      user_type: userType,
      role: mappedRole,
      first_name: user?.first_name,
      last_name: user?.last_name,
      phone: user?.phone,
      is_active: user?.is_active,
      last_login: user?.last_login,
      must_change_password: user?.must_change_password,
      password_changed_at: user?.password_changed_at,
      profile: user,
      loginTime: new Date()?.toISOString(),
    };
    localStorage.setItem('strapi_jwt_token', jwt);
    localStorage.setItem('user_data', JSON.stringify(userData));
    return {
      redirectRoute: getRoleBasedRedirectRoute(userType),
      mustChangePassword: user?.must_change_password === true,
      userData,
    };
  },

  // Login with enhanced role mapping according to new API documentation
  login: async (identifier, password) => {
    try {
      // Check if login is currently blocked
      if (loginAttemptTracker?.isLoginBlocked()) {
        const remainingMinutes = loginAttemptTracker?.getBlockTimeRemaining();
        return {
          success: false,
          error: `Demasiados intentos fallidos. Intente nuevamente en ${remainingMinutes} minuto${remainingMinutes !== 1 ? 's' : ''}`,
          isBlocked: true,
          blockTimeRemaining: remainingMinutes,
          canReset: true
        };
      }
      
      // Enhanced input validation
      if (!identifier || !password) {
        return {
          success: false,
          error: 'Usuario y contraseña son requeridos'
        };
      }

      const cleanIdentifier = identifier?.toString()?.trim();
      const cleanPassword = password?.toString()?.trim();

      if (!cleanIdentifier || !cleanPassword) {
        return {
          success: false,
          error: 'Usuario y contraseña no pueden estar vacíos'
        };
      }

      if (cleanPassword?.length < 3) {
        return {
          success: false,
          error: 'La contraseña debe tener al menos 3 caracteres'
        };
      }

      // Check for provided credentials specifically
      const isProvidedCredentials = cleanIdentifier === 'admin@innovaciondigital.mx';
      
      console.log('🔐 Starting authentication process...', {
        identifier: cleanIdentifier,
        isProvidedCredentials: isProvidedCredentials,
        attempts: loginAttemptTracker?.attempts,
        serverUrl: import.meta.env?.VITE_STRAPI_API_URL || 'Not configured'
      });

      // CRITICAL FIX: Clear any existing invalid tokens before login attempt
      if (loginAttemptTracker?.attempts > 0) {
        console.log('🧹 Clearing potentially invalid token before retry...');
        localStorage.removeItem('strapi_jwt_token');
      }

      const response = await strapiService?.auth?.login(cleanIdentifier, cleanPassword);
      
      console.log('📡 Raw Strapi response:', {
        status: response ? 'received' : 'null',
        hasJwt: !!response?.jwt,
        hasUser: !!response?.user,
        userType: response?.user?.user_type,
        userEmail: response?.user?.email,
        firstName: response?.user?.first_name,
        lastName: response?.user?.last_name
      });
      
      if (response?.jwt && response?.user) {
        // Reset failed attempt tracking on successful login
        loginAttemptTracker?.reset();
        
        // Use user_type from API response according to documentation
        const userType = response?.user?.user_type || 'authenticated';
        const mappedRole = mapStrapiRoleToAppRole(userType, response?.user?.email);
        
        console.log('🔄 Role mapping:', {
          apiUserType: userType,
          mappedRole: mappedRole,
          userEmail: response?.user?.email
        });
        
        const userData = {
          id: response?.user?.id,
          documentId: response?.user?.documentId,
          username: response?.user?.username,
          email: response?.user?.email,
          user_type: userType, // Store original user_type from API
          role: mappedRole, // Mapped role for app navigation
          first_name: response?.user?.first_name,
          last_name: response?.user?.last_name,
          phone: response?.user?.phone,
          is_active: response?.user?.is_active,
          last_login: response?.user?.last_login,
          // ✅ MANDATORY PASSWORD CHANGE - Store field from API response
          must_change_password: response?.user?.must_change_password,
          password_changed_at: response?.user?.password_changed_at,
          profile: response?.user,
          loginTime: new Date()?.toISOString()
        };

        localStorage.setItem('strapi_jwt_token', response?.jwt);
        localStorage.setItem('user_data', JSON.stringify(userData));
        
        // Determine redirect based on user_type from API
        const redirectRoute = getRoleBasedRedirectRoute(userType);
        
        console.log('✅ Authentication successful for:', cleanIdentifier, {
          userType: userType,
          mappedRole: mappedRole,
          redirectRoute: redirectRoute,
          mustChangePassword: userData?.must_change_password
        });
        
        return {
          success: true,
          user: userData,
          token: response?.jwt,
          redirectRoute: redirectRoute
        };
      }
      
      throw new Error('Invalid response from authentication server');
      
    } catch (error) {
      // Comprehensive error logging for server-side validation failures
      const isProvidedCredentials = identifier === 'admin@innovaciondigital.mx';
      
      console.error('❌ Authentication failed:', {
        identifier: identifier,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        attempts: loginAttemptTracker?.attempts,
        isProvidedCredentials: isProvidedCredentials,
        errorData: error?.response?.data,
        errorMessage: error?.message,
        networkError: !error?.response && !!error?.request,
        serverUrl: import.meta.env?.VITE_STRAPI_API_URL,
        wasTokenCleared: loginAttemptTracker?.attempts > 0
      });
      
      // Don't record as failed attempt if it's a network error
      if (error?.response) {
        loginAttemptTracker?.recordFailedAttempt(identifier);
      }
      
      // Extract error details with enhanced server diagnostics
      let errorMessage = 'Unknown error';
      let statusCode = null;

      if (error?.response) {
        statusCode = error?.response?.status;
        errorMessage = error?.response?.data?.error?.message || 
                     error?.response?.data?.message ||
                     error?.response?.data?.details?.errors?.[0]?.message ||
                     error?.response?.statusText ||
                     `HTTP ${statusCode} Error`;
                     
        // Enhanced debugging for provided credentials with 403 fix
        if (isProvidedCredentials) {
          console.log('🔍 Server-side validation diagnostics for admin@innovaciondigital.mx:', {
            serverUrl: import.meta.env?.VITE_STRAPI_API_URL,
            endpoint: error?.config?.url,
            method: error?.config?.method,
            requestPayload: error?.config?.data ? JSON.parse(error?.config?.data) : null,
            responseStatus: error?.response?.status,
            responseHeaders: error?.response?.headers,
            responseData: error?.response?.data,
            hadAuthHeader: error?.config?.headers?.Authorization ? 'YES - This was the problem!' : 'No',
            possibleCauses: error?.response?.status === 403 ? [
              '✅ FIXED: Invalid auth token was being sent with login request',
              'User account may still be inactive or blocked',  
              'Double-check password is exactly: admin123',
              'Server authentication configuration issue'
            ] : [
              'User does not exist in Strapi backend',
              'User account is inactive or blocked',  
              'Password is incorrect',
              'Server authentication configuration issue'
            ]
          });
        }
      } else if (error?.request) {
        errorMessage = 'network error';
        console.error('🌐 Network error detected - unable to reach Strapi server');
      } else {
        errorMessage = error?.message || 'unknown error';
      }
      
      // Enhanced context for error translation
      const context = {
        hasTestCredentials: loginAttemptTracker?.hasUsedTestCredentials(),
        attempts: loginAttemptTracker?.attempts,
        isProvidedCredentials: isProvidedCredentials,
        serverUrl: import.meta.env?.VITE_STRAPI_API_URL,
        was403Fixed: error?.response?.status === 403
      };
      
      const translatedError = translateStrapiError(errorMessage, statusCode, context);
      
      // Comprehensive error message for provided credentials with 403 fix
      let finalError = translatedError;
      let diagnosticsInfo = null;
      
      if (isProvidedCredentials && statusCode === 403) {
        finalError = '🔧 Error 403 corregido: Se eliminó el token inválido que se enviaba con las credenciales de login. Intente nuevamente.';
      } else if (isProvidedCredentials && statusCode === 400) {
        finalError = 'Las credenciales admin@innovaciondigital.mx fueron rechazadas por el servidor Strapi';
        
        diagnosticsInfo = {
          server: import.meta.env?.VITE_STRAPI_API_URL || 'URL no configurada',
          endpoint: '/api/auth/local',
          status: 'Credenciales rechazadas por el servidor',
          possibleSolutions: [
            '1. Verifique que el usuario admin@innovaciondigital.mx existe en el panel de administración de Strapi',
            '2. Confirme que la contraseña "admin123" es correcta para este usuario',
            '3. Asegúrese de que el usuario esté activo y no bloqueado',
            '4. Verifique la configuración de autenticación del backend Strapi',
            '5. Revise los logs del servidor Strapi para más detalles sobre el error de validación'
          ]
        };
      }
      
      // Add attempt counter for user awareness (only for response errors)
      if (error?.response && loginAttemptTracker?.attempts >= 2 && loginAttemptTracker?.attempts < 5) {
        finalError += ` (Intento ${loginAttemptTracker?.attempts} de 5)`;
      }
      
      return {
        success: false,
        error: finalError,
        attempts: loginAttemptTracker?.attempts,
        statusCode: statusCode,
        canReset: loginAttemptTracker?.attempts >= 3,
        testCredentialsDetected: context?.hasTestCredentials,
        diagnosticsInfo: diagnosticsInfo,
        serverValidationFailed: isProvidedCredentials && statusCode === 400,
        was403Fixed: statusCode === 403
      };
    }
  },

  // Register new user
  register: async (userData) => {
    try {
      console.log('Starting registration process...');
      const response = await strapiService?.auth?.register(userData);
      
      if (response?.jwt && response?.user) {
        const userProfile = {
          id: response?.user?.id,
          username: response?.user?.username,
          email: response?.user?.email,
          role: response?.user?.role?.name || 'authenticated',
          profile: response?.user,
          loginTime: new Date()?.toISOString()
        };

        localStorage.setItem('strapi_jwt_token', response?.jwt);
        localStorage.setItem('user_data', JSON.stringify(userProfile));
        
        return {
          success: true,
          user: userProfile,
          token: response?.jwt
        };
      }
      
      throw new Error('Registration failed');
    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMessage = error?.response?.data?.error?.message || 
                           error?.response?.data?.message || 
                           error?.message || 
                           'Unknown error';
      let statusCode = error?.response?.status;
      
      return {
        success: false,
        error: translateStrapiError(errorMessage, statusCode)
      };
    }
  },

  logout: () => {
    console.log('Logging out user...');
    loginAttemptTracker?.reset();
    localStorage.removeItem('strapi_jwt_token');
    localStorage.removeItem('user_data');
    window.location.href = '/login';
  },

  getCurrentUser: () => {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('strapi_jwt_token');
    const userData = localStorage.getItem('user_data');
    return !!(token && userData);
  },

  getToken: () => {
    return localStorage.getItem('strapi_jwt_token');
  },

  // Enhanced role checking with new user types
  hasRole: (requiredRole) => {
    const user = authService?.getCurrentUser();
    return user?.role === requiredRole;
  },

  hasUserType: (requiredUserType) => {
    const userData = authService?.getCurrentUser();
    return userData?.user_type === requiredUserType;
  },

  hasAnyRole: (roles = []) => {
    const user = authService?.getCurrentUser();
    return roles?.includes(user?.role);
  },

  hasAnyUserType: (userTypes = []) => {
    const userData = authService?.getCurrentUser();
    return userTypes?.includes(userData?.user_type);
  },

  // New method to get redirect route for current user
  getUserRedirectRoute: () => {
    const user = authService?.getCurrentUser();
    if (user?.role) {
      return getRoleBasedRedirectRoute(user?.role);
    }
    return '/app/system-analytics';
  },

  // NEW: Get user profile with companies and employees according to API documentation
  getUserProfile: async () => {
    // Guard: sin sesión no hay nada que pedir. Evita el 401 + "Get user profile
    // with companies failed" en consola cuando un componente monta durante la
    // transición de login/logout.
    if (!authService?.isAuthenticated()) {
      console.log('⏭️ getUserProfile omitido: no hay sesión activa');
      return null;
    }
    try {
      console.log('🔍 Fetching user profile with companies and employees...');
      const profileData = await strapiService?.auth?.getUserProfile();
      
      if (profileData?.user && profileData?.companies) {
        console.log('✅ Profile data received:', {
          userType: profileData?.user?.user_type,
          firstName: profileData?.user?.first_name,
          lastName: profileData?.user?.last_name,
          companiesCount: profileData?.companies?.length,
          totalEmployees: profileData?.companies?.reduce((total, company) => 
            total + (company?.employees?.length || 0), 0)
        });

        // Update stored user data with profile information
        const currentUserData = authService?.getCurrentUser();
        const updatedUserData = {
          ...currentUserData,
          ...profileData?.user,
          companies: profileData?.companies,
          profileLoadTime: new Date()?.toISOString()
        };

        localStorage.setItem('user_data', JSON.stringify(updatedUserData));
        
        return {
          user: profileData?.user,
          companies: profileData?.companies
        };
      }
      
      throw new Error('Invalid profile data received');
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      return null;
    }
  },

  // NEW: Get companies data for current user
  getUserCompanies: () => {
    const userData = authService?.getCurrentUser();
    return userData?.companies || [];
  },

  // NEW: Get user type from stored data
  getUserType: () => {
    const userData = authService?.getCurrentUser();
    return userData?.user_type || 'authenticated';
  },

  // NEW: Password change method with comprehensive error handling
  changePassword: async (currentPassword, newPassword) => {
    try {
      console.log('🔒 Starting password change process...');
      
      // Client-side validation
      if (!currentPassword || !newPassword) {
        return {
          success: false,
          error: 'Todos los campos son obligatorios'
        };
      }

      if (newPassword?.length < 6) {
        return {
          success: false,
          error: 'La contraseña debe tener al menos 6 caracteres'
        };
      }

      const response = await strapiService?.auth?.changePassword(currentPassword, newPassword);
      
      if (response?.message) {
        console.log('✅ Password changed successfully');
        
        // ✅ UPDATE USER STATE - Remove must_change_password flag
        const currentUserData = authService?.getCurrentUser();
        if (currentUserData) {
          const updatedUserData = {
            ...currentUserData,
            must_change_password: false,
            password_changed_at: new Date()?.toISOString()
          };
          
          localStorage.setItem('user_data', JSON.stringify(updatedUserData));
          console.log('🔄 User state updated - must_change_password set to false');
        }
        
        return {
          success: true,
          message: 'Contraseña cambiada exitosamente'
        };
      }
      
      throw new Error('Invalid response from password change endpoint');
      
    } catch (error) {
      console.error('❌ Password change failed:', error);
      
      let errorMessage = 'Error al cambiar contraseña';
      
      if (error?.response) {
        let statusCode = error?.response?.status;
        const serverMessage = error?.response?.data?.message || error?.response?.data?.error?.message;
        
        // Enhanced error handling according to API documentation
        switch (statusCode) {
          case 400:
            if (serverMessage?.includes('Current password and new password are required')) {
              errorMessage = 'Todos los campos son obligatorios';
            } else if (serverMessage?.includes('New password must be at least 6 characters long')) {
              errorMessage = 'La contraseña debe tener al menos 6 caracteres';
            } else if (serverMessage?.includes('Current password is incorrect')) {
              errorMessage = 'La contraseña actual es incorrecta';
            } else {
              errorMessage = 'Datos inválidos. Verifique la información proporcionada';
            }
            break;
          case 401:
            errorMessage = 'No autorizado. Inicie sesión nuevamente';
            break;
          case 403:
            errorMessage = 'Acceso denegado para cambiar contraseña';
            break;
          default:
            errorMessage = serverMessage || 'Error del servidor al cambiar contraseña';
        }
      } else if (error?.request) {
        errorMessage = 'Error de conexión. Verifique su conexión a internet';
      } else {
        errorMessage = error?.message || 'Error desconocido al cambiar contraseña';
      }
      
      return {
        success: false,
        error: errorMessage,
        statusCode: error?.response?.status
      };
    }
  },

  // NEW: Check if user must change password
  mustChangePassword: () => {
    const userData = authService?.getCurrentUser();
    return userData?.must_change_password === true;
  },

  refreshProfile: async () => {
    try {
      const profile = await strapiService?.auth?.getProfile();
      
      const userData = {
        id: profile?.id,
        username: profile?.username,
        email: profile?.email,
        role: profile?.role?.name || 'authenticated',
        profile: profile,
        loginTime: new Date()?.toISOString()
      };

      localStorage.setItem('user_data', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      return null;
    }
  },

  // Enhanced login attempt status
  getLoginAttemptStatus: () => {
    return {
      attempts: loginAttemptTracker?.attempts,
      isBlocked: loginAttemptTracker?.isBlocked,
      blockTimeRemaining: loginAttemptTracker?.getBlockTimeRemaining(),
      hasUsedTestCredentials: loginAttemptTracker?.hasUsedTestCredentials(),
      testCredentialsMessage: loginAttemptTracker?.getTestCredentialsMessage(),
      canReset: loginAttemptTracker?.attempts >= 3
    };
  },

  // Enhanced reset with feedback
  resetLoginAttempts: () => {
    console.log('🔄 Resetting login attempts...');
    loginAttemptTracker?.reset();
    console.log('✅ Login attempts reset successfully');
    return {
      success: true,
      message: 'Intentos de inicio de sesión reiniciados correctamente'
    };
  }
};

export default authService;